import { useEffect, useState } from 'react'
import toast from '@/components/ui/toast'
import Notification from '@/components/ui/Notification'
import { rdaNotify } from '@/custolink-views/asset_frontend/redesigns/RdaToast'
import classNames from '@/utils/classNames'
import useUiContext from '@/custolink-hooks/useUiContext'
import RdaDrawer from '@/custolink-views/asset_frontend/redesigns/RdaDrawer'
import AssetDrawerScope from '@/custolink-views/asset_frontend/redesigns/AssetDrawerScope'
import { normaliseHex } from '@/custolink-views/asset_frontend/redesigns/theme'
import { useAssetFrontendDesign } from '@/custolink-views/asset_frontend/redesigns/assetFrontendDesign'
import {
    FormField,
    PrimaryButton,
    SecondaryButton,
    SelectField,
    TextAreaField,
} from '@/custolink-views/asset_frontend/redesigns/formKit'
import {
    PiArrowClockwise,
    PiCheck,
    PiCopy,
    PiThumbsDown,
    PiThumbsDownFill,
    PiThumbsUp,
    PiThumbsUpFill,
} from 'react-icons/pi'
import type { FormEvent } from 'react'

type ChatCustomActionProps = {
    content: string
}

/**
 * The retry affordance under a failed answer. Unlike the copy/feedback row,
 * this one stays visible without a hover - a recovery action should not
 * depend on the visitor finding it - and uses the same error tone as the
 * note it sits under (see `Message.tsx`'s error branch).
 */
export function RetryAction({ onRetry }: { onRetry: () => void }) {
    return (
        <button
            type="button"
            className={classNames(
                'inline-flex items-center gap-1.5 self-start text-xs font-semibold text-[#7A1F17]',
                'underline decoration-[#7A1F17]/40 underline-offset-2 hover:decoration-[#7A1F17]',
                'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current',
            )}
            onClick={onRetry}
        >
            <PiArrowClockwise aria-hidden className="text-sm" />
            Try again
        </button>
    )
}

const responseOption = [
    'Not factually correct',
    'Harmful content',
    'Overreactive refusal',
    'Other',
]

// Thumbs up/down stay hidden, as they were before the redesign: there is no
// feedback endpoint yet, so the form would only throw the visitor's input away.
// Flip this once feedback is actually stored.
const FEEDBACK_ENABLED = false

const ACTION_BTN =
    'flex h-8 w-8 items-center justify-center rounded-full text-base text-[var(--rda-muted)] ' +
    'transition-colors hover:bg-[var(--rda-neutral)] hover:text-[var(--rda-on-neutral)] ' +
    'outline-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-current'

/**
 * Copy / thumbs up / thumbs down under an AI reply, and the thumbs-down (or
 * up) feedback follow-up. Rebuilt on the design's tokens: the Ecme `Tooltip`
 * is a `title`/`aria-label` on the button itself (no floating popup needed
 * for a single icon action), and the Ecme `Dialog` is the design's bottom
 * sheet (`RdaDrawer`, same as `FirstTimeSheet`/`NudgeSerialSheet`).
 *
 * There is no feedback endpoint behind this - the original only toasted and
 * kept local state - so that stays exactly as it was, just restyled.
 */
const ChatCustomAction = ({ content }: ChatCustomActionProps) => {
    const uic = useUiContext()
    const newDesign = useAssetFrontendDesign()
    const accent = normaliseHex(uic.organisationContext.lookAndFeel.assetFrontendBgcolorHex)

    const [copied, setCopied] = useState(false)
    const [selected, setSelected] = useState('')
    const [responseSend, setResponseSend] = useState('')
    const [responseDialog, setResponseDialog] = useState<{
        type: string
        open: boolean
    }>({
        type: '',
        open: false,
    })

    useEffect(() => {
        if (copied && content) {
            navigator.clipboard.writeText(content)
            const copyFeedbackInterval = setTimeout(
                () => setCopied(false),
                2000,
            )

            return () => {
                clearTimeout(copyFeedbackInterval)
            }
        }
    }, [copied, content])

    const handleDialogClose = () => {
        setResponseDialog({
            type: '',
            open: false,
        })
    }

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault()
        setResponseSend(responseDialog.type)
        handleDialogClose()
        if (newDesign) {
            rdaNotify.success('Thanks for your feedback!')
        } else {
            toast.push(
                <Notification type="success">Thanks for your feedback!</Notification>,
                { placement: 'top-center' },
            )
        }
    }

    return (
        <>
            <div className="mt-0.5 flex items-center gap-0.5">
                <button
                    type="button"
                    title={copied ? 'Copied' : 'Copy'}
                    aria-label={copied ? 'Copied' : 'Copy response'}
                    className={classNames(ACTION_BTN, 'text-lg')}
                    onClick={() => setCopied(true)}
                >
                    {copied ? (
                        <PiCheck className="text-emerald-600" />
                    ) : (
                        <PiCopy />
                    )}
                </button>
                {FEEDBACK_ENABLED && (
                    <>
                <button
                    type="button"
                    title="Good response"
                    aria-label="Good response"
                    aria-pressed={responseSend === 'praise'}
                    className={classNames(ACTION_BTN, 'text-lg')}
                    onClick={() =>
                        setResponseDialog({ type: 'praise', open: true })
                    }
                >
                    {responseSend === 'praise' ? (
                        <PiThumbsUpFill className="text-[var(--rda-accent-text)]" />
                    ) : (
                        <PiThumbsUp />
                    )}
                </button>
                <button
                    type="button"
                    title="Bad response"
                    aria-label="Bad response"
                    aria-pressed={responseSend === 'blame'}
                    className={classNames(ACTION_BTN, 'text-lg')}
                    onClick={() =>
                        setResponseDialog({ type: 'blame', open: true })
                    }
                >
                    {responseSend === 'blame' ? (
                        <PiThumbsDownFill className="text-[var(--rda-accent-text)]" />
                    ) : (
                        <PiThumbsDown />
                    )}
                </button>
                    </>
                )}
            </div>

            <RdaDrawer
                title="Feedback"
                isOpen={responseDialog.open}
                placement="bottom"
                height="auto"
                onClose={handleDialogClose}
                onRequestClose={handleDialogClose}
            >
                <AssetDrawerScope fit accent={accent}>
                    <form
                        className="mx-auto flex w-full max-w-xl flex-col gap-1"
                        onSubmit={handleSubmit}
                    >
                        {responseDialog.type === 'praise' && (
                            <FormField label="Please provide details (optional)">
                                <TextAreaField
                                    rows={3}
                                    placeholder="What was satisfying about this response?"
                                />
                            </FormField>
                        )}
                        {responseDialog.type === 'blame' && (
                            <>
                                <FormField label="What type of issue do you wish to report? (optional)">
                                    <SelectField
                                        placeholder="Select an issue"
                                        options={responseOption}
                                        value={selected}
                                        onChange={(e) => setSelected(e.target.value)}
                                    />
                                </FormField>
                                <FormField label="Please provide details (optional)">
                                    <TextAreaField
                                        rows={3}
                                        placeholder="What was unsatisfying about this response?"
                                    />
                                </FormField>
                            </>
                        )}
                        <div className="mt-3 flex flex-col gap-2">
                            <PrimaryButton
                                type="submit"
                                className="rounded-lg sm:rounded-xl"
                            >
                                Submit
                            </PrimaryButton>
                            <SecondaryButton
                                type="button"
                                className="rounded-lg sm:rounded-xl"
                                onClick={handleDialogClose}
                            >
                                Cancel
                            </SecondaryButton>
                        </div>
                    </form>
                </AssetDrawerScope>
            </RdaDrawer>
        </>
    )
}

export default ChatCustomAction
